import { AuthorizationDocument } from '../../Authorization.graphql';
import {
  GqlAuthorizationGroup,
  GqlCompany,
  GqlMeeting,
  GqlMeetingConcern,
} from '../../../../graphql/base-types.graphql';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Trans } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { toDateTime, toDateTimeMaybe } from '@startuptools/common/common';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { agmPeriod, fiscalPeriod, isBetween } from '../../../../helpers/agm';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { useAgSetValidThruMutation } from '../../../../graphql/react-operations';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';

type FormValues = { validThruAgmYear: string | 'other'; validThruDate: DateTime | null };

export const AgStepValidThru = ({
  company,
  agmMeetings,
  authorizationGroup,
}: {
  company: Pick<GqlCompany, 'id' | 'fiscalYear'>;
  agmMeetings: Pick<GqlMeeting, 'id' | 'startTime'>[];
  authorizationGroup: Pick<GqlAuthorizationGroup, 'id' | 'validThruAgmYear' | 'validThruDate'> & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id'> & {
          meeting: Pick<GqlMeeting, 'id' | 'kind' | 'startTime'>;
        })
      | null;
  };
}) => {
  const date =
    toDateTimeMaybe(authorizationGroup.meetingConcern?.meeting.startTime)?.startOf('day') ??
    DateTime.now().startOf('day');

  const yearLabel = {
    thisYear: date.toFormat('yyyy'),
    nextYear: date.plus({ year: 1 }).toFormat('yyyy'),
  };

  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const [doSubmit, { loading }] = useAgSetValidThruMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const { handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: {
      validThruAgmYear:
        authorizationGroup.validThruAgmYear ?? (authorizationGroup.validThruDate ? 'other' : yearLabel.nextYear),
      validThruDate: toDateTimeMaybe(authorizationGroup.validThruDate),
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      if (values.validThruAgmYear !== 'other') {
        await doSubmit({
          variables: {
            companyId: company.id,
            input: { id: authorizationGroup.id, validThruAgmYear: values.validThruAgmYear, validThruDate: null },
          },
        });
      } else {
        await doSubmit({
          variables: {
            companyId: company.id,
            input: {
              id: authorizationGroup.id,
              validThruAgmYear: null,
              validThruDate: values.validThruDate!.toUTC(0, { keepLocalTime: true }).toString(),
            },
          },
        });
      }
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
    return;
  };

  const canSelectThisYear = useMemo(() => {
    const fp = fiscalPeriod(company.fiscalYear, date);
    return !agmMeetings.some(m => isBetween(fp, toDateTime(m.startTime)));
  }, [company.fiscalYear, date, agmMeetings]);

  const maxDate = useMemo(() => {
    const year = date.plus({ year: 1 }).startOf('year');
    return agmPeriod(company.fiscalYear, year).start;
  }, [date, company.fiscalYear]);

  const validThruAgmYear = watch('validThruAgmYear');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="For how long shall the authorization be valid?" />
        </Typography>

        <Alert>
          <Trans id="An authorization can either be valid until a specific date or to the next annual general meeting. If you set a specific date, it may not be later than the latest date for the annual general meeting. In a company with calendar year as its fiscal year, the annual general meeting must not be held later than June 30th the year after. This means that June 30th is the latest date until which an authorization may be valid. But even if an authorization is set to be valid until June 30th, it will expire as soon as the annual general meeting is held." />
        </Alert>

        <Typography level="title-md">
          <Trans id="The authorization should be valid..." />
        </Typography>

        <Controller
          control={control}
          name="validThruAgmYear"
          render={({ field }) => (
            <RadioGroup {...field}>
              <Radio
                disabled={!canSelectThisYear}
                value={yearLabel.thisYear}
                label={
                  <Trans id="until the next annual general meeting {year}" values={{ year: yearLabel.thisYear }} />
                }
              />
              <Radio
                value={yearLabel.nextYear}
                label={
                  <Trans id="until the next annual general meeting {year}" values={{ year: yearLabel.nextYear }} />
                }
              />
              <Radio value="other" label={<Trans id="until a different date..." />} />
            </RadioGroup>
          )}
        />

        {validThruAgmYear === 'other' && (
          <Controller
            control={control}
            name="validThruDate"
            rules={{ required: true }}
            render={({ field }) => (
              <FormControl sx={{ maxWidth: 300 }} required>
                <FormLabel>
                  <Trans id="Choose a Date" />
                </FormLabel>
                <DateInput
                  {...field}
                  minDate={toDateTimeMaybe(authorizationGroup.meetingConcern?.meeting.startTime) ?? undefined}
                  maxDate={maxDate}
                />
              </FormControl>
            )}
          />
        )}

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
